import {
  newTeamAssistV2Answer,
  reduceTeamAssistV2Event,
  TeamAssistV2ProposalDecision,
} from 'actions/team_assist_v2/lib/team_assist_v2_answer';
import { WRITE_INTERVAL_MS } from 'actions/team_assist_v2/lib/streaming_team_assist_v2_turn';
import { turnWithProposal } from 'actions/team_assist_v2/lib/team_assist_v2_proposals';
import TeamAssistV2Turn from 'models/team_assist_v2_turn';

/**
 * A proposed action's run, streamed into the turn that proposed it. Starting
 * marks the proposal approved and the turn `live`. The run's `tool_executed`,
 * its first event, settles the proposal; the answer after it is added to the
 * turn's `followUps`, and the turn stops being `live` at that answer's `done`
 * or `error` event, or when the run ends some other way. An `error` before
 * `tool_executed` ends it at once: glados called the app, or may have, but
 * couldn't record the outcome. Once `signal` aborts, it never writes again.
 */
export default class StreamingTeamAssistV2FollowUp {
  constructor({ store, signal, turnId, toolUseId, input }) {
    this.store = store;
    this.signal = signal;
    this.turnId = turnId;
    this.toolUseId = toolUseId;
    this.executed = null;
    this.answer = null;
    this.live = true;
    this.writeTimer = null;
    this.failedBeforeRun = false;
    this.priorFollowUpCount = store.find(turnId).followUps.length;
    this._write(proposal => ({
      ...proposal,
      decision: TeamAssistV2ProposalDecision.approved,
      approvedInput: input,
      issues: null,
      refusal: null,
    }));
  }

  // Whether the run's outcome arrived, so glados has it recorded.
  get hasRun() {
    return this.executed !== null;
  }

  // Events before the run's outcome aren't part of the answer that follows it.
  apply(event) {
    if (this.signal.aborted || !this.live) {
      return;
    }
    if (!this.hasRun) {
      if (event.type === 'tool_executed' && event.toolUseId === this.toolUseId) {
        this.executed = event;
        this.answer = newTeamAssistV2Answer();
        this._write();
      } else if (event.type === 'error') {
        this.failedBeforeRun = true;
        this._end();
      }
      return;
    }
    this.answer = reduceTeamAssistV2Event(this.answer, event);
    if (event.type !== 'done' && event.type !== 'error') {
      this._scheduleWrite();
      return;
    }
    this.live = false;
    this._dropLoadedCopy(this.answer.questionId);
    this._write();
  }

  // Fails the answer that follows a run.
  fail(code) {
    this.apply({ type: 'error', code });
  }

  // Ends a run whose outcome never arrived. The proposal stays running, as
  // glados may have run it, until a session load says what became of it.
  stop() {
    this._end();
  }

  // Ends a run glados refused before starting it, so the proposal is the
  // agent's to decide again. `refusal` is glados's code for why, when the card
  // shows it, and `issues` what glados found wrong in the input.
  refuse({ refusal = null, issues = null } = {}) {
    this._end(proposal => ({ ...proposal, decision: null, approvedInput: null, issues, refusal }));
  }

  // Ends a run glados refused because the proposal was already decided:
  // `decision` is what glados has, until a session load says more.
  decided(decision) {
    this._end(proposal => ({ ...proposal, decision, issues: null, refusal: null }));
  }

  _end(proposalFn) {
    if (this.signal.aborted || !this.live) {
      return;
    }
    this.live = false;
    this._write(proposalFn);
  }

  _scheduleWrite() {
    if (this.writeTimer === null) {
      this.writeTimer = setTimeout(() => this._write(), WRITE_INTERVAL_MS);
    }
  }

  _write(proposalFn = null) {
    clearTimeout(this.writeTimer);
    this.writeTimer = null;
    if (this.signal.aborted || !this.store.has({ id: this.turnId })) {
      return;
    }
    const turn = this.store.find(this.turnId).toJs();
    const followUps = turn.followUps.slice(0, this.priorFollowUpCount).map(this._settle.bind(this));
    const next = {
      ...turn,
      answer: turn.answer && this._settle(turn.answer),
      followUps: this.answer ? [...followUps, this.answer] : followUps,
      live: this.live,
    };
    this.store.addOrReplace(
      TeamAssistV2Turn.create(proposalFn ? turnWithProposal(next, this.toolUseId, proposalFn) : next)
    );
  }

  _settle(answer) {
    return this.executed ? reduceTeamAssistV2Event(answer, this.executed) : answer;
  }

  // A session loaded while this streamed can hold a copy of a turn asked
  // here, under glados's id for its question rather than this turn's.
  _dropLoadedCopy(questionId) {
    if (!questionId || questionId === this.turnId || !this.store.has({ id: questionId })) {
      return;
    }
    this.store.set(this.store.findAll().filter(turn => turn.id !== questionId));
  }
}
